import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Plus, Edit, Archive, RotateCcw, Eye } from 'lucide-react';
import ConfirmDialog from '@/components/ConfirmDialog';
import { useAuth } from '@/contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Consultations = () => {
  const { user } = useAuth();
  const canWrite = user?.role === 'admin' || user?.role === 'doctor';
  const [consultations, setConsultations] = useState([]);
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingConsultation, setEditingConsultation] = useState(null);
  const [showPrescriptionForm, setShowPrescriptionForm] = useState(false);
  const [selectedConsultation, setSelectedConsultation] = useState(null);
  const [medicines, setMedicines] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [formData, setFormData] = useState({
    patientId: '',
    doctorId: '',
    symptoms: '',
    diagnosis: '',
    notes: '',
  });
  const [prescriptionData, setPrescriptionData] = useState({
    medicines: [{ medicineId: '', medicineName: '', dosage: '', duration: '', instructions: '' }],
  });

  const fetchConsultations = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/consultations?include_archived=${showArchived}`);
      setConsultations(response.data);
    } catch (error) {
      console.error('Error fetching consultations:', error);
    }
  }, [showArchived]);

  const fetchPatients = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/patients`);
      setPatients(response.data);
    } catch (error) {
      console.error('Error fetching patients:', error);
    }
  }, []);

  const fetchDoctors = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/doctors`);
      setDoctors(response.data);
    } catch (error) {
      console.error('Error fetching doctors:', error);
    }
  }, []);

  const fetchMedicines = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/medicines`);
      setMedicines(response.data);
    } catch (error) {
      console.error('Error fetching medicines:', error);
    }
  }, []);

  useEffect(() => {
    fetchConsultations();
  }, [fetchConsultations]);

  useEffect(() => {
    fetchPatients();
    fetchDoctors();
    fetchMedicines();
  }, [fetchPatients, fetchDoctors, fetchMedicines]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingConsultation) {
        await axios.put(`${API}/consultations/${editingConsultation.id}`, formData);
      } else {
        await axios.post(`${API}/consultations`, formData);
      }
      fetchConsultations();
      resetForm();
    } catch (error) {
      console.error('Error saving consultation:', error);
      alert(error.response?.data?.detail || 'Failed to save consultation');
    }
  };

  const handleEdit = (consultation) => {
    setEditingConsultation(consultation);
    setFormData({
      patientId: consultation.patientId,
      doctorId: consultation.doctorId,
      symptoms: consultation.symptoms,
      diagnosis: consultation.diagnosis,
      notes: consultation.notes || '',
    });
    setShowForm(true);
  };

  const handleArchive = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await axios.delete(`${API}/consultations/${deleteTarget.id}`);
      setDeleteTarget(null);
      fetchConsultations();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to archive consultation');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleRestore = async (consultation) => {
    try {
      await axios.post(`${API}/consultations/${consultation.id}/restore`);
      fetchConsultations();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to restore consultation');
    }
  };

  const handlePrescriptionSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/prescriptions`, {
        consultationId: selectedConsultation.id,
        medicines: prescriptionData.medicines,
      });
      alert('Prescription saved successfully');
      setShowPrescriptionForm(false);
      setSelectedConsultation(null);
      setPrescriptionData({ medicines: [{ medicineId: '', medicineName: '', dosage: '', duration: '', instructions: '' }] });
    } catch (error) {
      console.error('Error saving prescription:', error);
    }
  };

  const addMedicineRow = () => {
    setPrescriptionData({
      medicines: [
        ...prescriptionData.medicines,
        { medicineId: '', medicineName: '', dosage: '', duration: '', instructions: '' },
      ],
    });
  };

  const updateMedicineRow = (index, field, value) => {
    const updated = [...prescriptionData.medicines];
    updated[index][field] = value;
    if (field === 'medicineId') {
      const medicine = medicines.find((m) => m.id === value);
      updated[index].medicineName = medicine?.name || '';
    }
    setPrescriptionData({ medicines: updated });
  };

  const removeMedicineRow = (index) => {
    const updated = prescriptionData.medicines.filter((_, i) => i !== index);
    setPrescriptionData({ medicines: updated });
  };

  const resetForm = () => {
    setFormData({ patientId: '', doctorId: '', symptoms: '', diagnosis: '', notes: '' });
    setShowForm(false);
    setEditingConsultation(null);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-bold" style={{ color: '#2D3A2A', fontFamily: 'Outfit, sans-serif' }} data-testid="consultations-title">
            Consultations
          </h1>
          <p className="mt-2" style={{ color: '#6B7280' }}>Manage patient consultations</p>
        </div>
        <button
          className="primary-btn flex items-center gap-2"
          onClick={() => setShowForm(true)}
          data-testid="add-consultation-btn"
          style={{ display: canWrite ? 'inline-flex' : 'none' }}
        >
          <Plus size={20} />
          New Consultation
        </button>
        {!canWrite && (
          <span
            className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium uppercase tracking-wide"
            style={{ backgroundColor: '#DBEAFE', color: '#1E40AF' }}
            data-testid="readonly-banner"
          >
            <Eye size={14} />
            Read-only
          </span>
        )}
      </div>

      {showForm && (
        <div className="card p-6 mb-8" data-testid="consultation-form">
          <h2 className="text-2xl font-bold mb-4" style={{ color: '#2D3A2A', fontFamily: 'Outfit, sans-serif' }}>
            {editingConsultation ? 'Edit Consultation' : 'New Consultation'}
          </h2>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label>Patient</label>
                <select
                  value={formData.patientId}
                  onChange={(e) => setFormData({ ...formData, patientId: e.target.value })}
                  required
                  data-testid="consultation-patient-select"
                >
                  <option value="">Select Patient</option>
                  {patients.map((patient) => (
                    <option key={patient.id} value={patient.id}>
                      {patient.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label>Doctor</label>
                <select
                  value={formData.doctorId}
                  onChange={(e) => setFormData({ ...formData, doctorId: e.target.value })}
                  required
                  data-testid="consultation-doctor-select"
                >
                  <option value="">Select Doctor</option>
                  {doctors.map((doctor) => (
                    <option key={doctor.id} value={doctor.id}>
                      {doctor.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label>Symptoms</label>
                <textarea
                  value={formData.symptoms}
                  onChange={(e) => setFormData({ ...formData, symptoms: e.target.value })}
                  rows={3}
                  required
                  data-testid="consultation-symptoms-input"
                />
              </div>
              <div className="md:col-span-2">
                <label>Diagnosis</label>
                <textarea
                  value={formData.diagnosis}
                  onChange={(e) => setFormData({ ...formData, diagnosis: e.target.value })}
                  rows={3}
                  required
                  data-testid="consultation-diagnosis-input"
                />
              </div>
              <div className="md:col-span-2">
                <label>Notes (Optional)</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={2}
                  data-testid="consultation-notes-input"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button type="submit" className="primary-btn" data-testid="consultation-submit-btn">
                {editingConsultation ? 'Update Consultation' : 'Save Consultation'}
              </button>
              <button type="button" className="secondary-btn" onClick={resetForm} data-testid="consultation-cancel-btn">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {showPrescriptionForm && selectedConsultation && (
        <div className="card p-6 mb-8" data-testid="prescription-form">
          <h2 className="text-2xl font-bold mb-4" style={{ color: '#2D3A2A', fontFamily: 'Outfit, sans-serif' }}>
            Add Prescription
          </h2>
          <form onSubmit={handlePrescriptionSubmit}>
            {prescriptionData.medicines.map((med, index) => (
              <div key={index} className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4 p-4 rounded" style={{ backgroundColor: '#F9F6F0' }}>
                <div>
                  <label>Medicine</label>
                  <select
                    value={med.medicineId}
                    onChange={(e) => updateMedicineRow(index, 'medicineId', e.target.value)}
                    required
                    data-testid={`prescription-medicine-${index}`}
                  >
                    <option value="">Select Medicine</option>
                    {medicines.map((medicine) => (
                      <option key={medicine.id} value={medicine.id}>
                        {medicine.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label>Dosage</label>
                  <input
                    type="text"
                    value={med.dosage}
                    onChange={(e) => updateMedicineRow(index, 'dosage', e.target.value)}
                    required
                    placeholder="e.g., 1 tablet"
                    data-testid={`prescription-dosage-${index}`}
                  />
                </div>
                <div>
                  <label>Duration</label>
                  <input
                    type="text"
                    value={med.duration}
                    onChange={(e) => updateMedicineRow(index, 'duration', e.target.value)}
                    required
                    placeholder="e.g., 7 days"
                    data-testid={`prescription-duration-${index}`}
                  />
                </div>
                <div>
                  <label>Instructions</label>
                  <input
                    type="text"
                    value={med.instructions}
                    onChange={(e) => updateMedicineRow(index, 'instructions', e.target.value)}
                    required
                    placeholder="e.g., After meals"
                    data-testid={`prescription-instructions-${index}`}
                  />
                </div>
                <div className="flex items-end">
                  <button
                    type="button"
                    onClick={() => removeMedicineRow(index)}
                    className="secondary-btn w-full"
                    data-testid={`remove-medicine-${index}`}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={addMedicineRow}
              className="secondary-btn mb-4"
              data-testid="add-medicine-row-btn"
            >
              + Add Medicine
            </button>
            <div className="flex gap-3 mt-6">
              <button type="submit" className="primary-btn" data-testid="prescription-submit-btn">
                Save Prescription
              </button>
              <button
                type="button"
                className="secondary-btn"
                onClick={() => {
                  setShowPrescriptionForm(false);
                  setSelectedConsultation(null);
                }}
                data-testid="prescription-cancel-btn"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="card p-6">
        <div className="mb-4 flex justify-end">
          <label className="flex items-center gap-2 cursor-pointer whitespace-nowrap text-sm" data-testid="show-archived-toggle">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="w-auto"
            />
            <Archive size={16} style={{ color: '#92400E' }} />
            <span style={{ color: '#2D3A2A' }}>Show archived</span>
          </label>
        </div>
        <div className="overflow-x-auto">
          <table data-testid="consultations-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Patient</th>
                <th>Doctor</th>
                <th>Symptoms</th>
                <th>Diagnosis</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {consultations.map((consultation) => {
                const patient = patients.find((p) => p.id === consultation.patientId);
                const doctor = doctors.find((d) => d.id === consultation.doctorId);
                return (
                  <tr key={consultation.id} data-testid={`consultation-row-${consultation.id}`} style={consultation.archived ? { backgroundColor: '#FEF3C7', opacity: 0.85 } : {}}>
                    <td>{new Date(consultation.date).toLocaleDateString()}</td>
                    <td className="font-medium" style={{ color: '#2D3A2A' }}>
                      <div className="flex items-center gap-2">
                        {patient?.name || 'Unknown'}
                        {consultation.archived && (
                          <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: '#FDE68A', color: '#92400E' }} data-testid={`archived-badge-${consultation.id}`}>
                            ARCHIVED
                          </span>
                        )}
                      </div>
                    </td>
                    <td>{doctor?.name || 'Unknown'}</td>
                    <td>{consultation.symptoms.substring(0, 50)}...</td>
                    <td>{consultation.diagnosis.substring(0, 50)}...</td>
                    <td>
                      <div className="flex gap-1 items-center">
                        {consultation.archived ? (
                          canWrite ? (
                            <button
                              onClick={() => handleRestore(consultation)}
                              className="p-2 rounded hover:bg-gray-100 transition-colors"
                              title="Restore"
                              data-testid={`restore-consultation-${consultation.id}`}
                            >
                              <RotateCcw size={18} style={{ color: '#065F46' }} />
                            </button>
                          ) : (
                            <span style={{ color: '#9CA3AF', fontSize: '0.85rem' }}>—</span>
                          )
                        ) : canWrite ? (
                          <>
                            <button
                              onClick={() => {
                                setSelectedConsultation(consultation);
                                setShowPrescriptionForm(true);
                              }}
                              className="primary-btn text-sm"
                              data-testid={`add-prescription-${consultation.id}`}
                            >
                              Prescription
                            </button>
                            <button
                              onClick={() => handleEdit(consultation)}
                              className="p-2 rounded hover:bg-gray-100 transition-colors"
                              title="Edit"
                              data-testid={`edit-consultation-${consultation.id}`}
                            >
                              <Edit size={18} style={{ color: '#990099' }} />
                            </button>
                            <button
                              onClick={() => setDeleteTarget(consultation)}
                              className="p-2 rounded hover:bg-gray-100 transition-colors"
                              title="Archive"
                              data-testid={`delete-consultation-${consultation.id}`}
                            >
                              <Archive size={18} style={{ color: '#92400E' }} />
                            </button>
                          </>
                        ) : (
                          <span style={{ color: '#9CA3AF', fontSize: '0.85rem' }} data-testid={`readonly-cell-${consultation.id}`}>View only</span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {consultations.length === 0 && (
            <div className="text-center py-8" style={{ color: '#6B7280' }} data-testid="no-consultations">
              No consultations found
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Archive Consultation"
        message="Are you sure you want to archive this consultation? It will be preserved and can be restored later. Use Data Cleanup for permanent deletion."
        confirmLabel="Archive Consultation"
        loading={deleteLoading}
        onConfirm={handleArchive}
        onCancel={() => setDeleteTarget(null)}
        testIdPrefix="delete-consultation"
      />
    </div>
  );
};

export default Consultations;
